import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import axios, { AxiosError } from 'axios';
import { FiPhone, FiSend } from 'react-icons/fi';
import { apiClient } from '../config/apiConfig';
import './AdminProducts.css';

interface Variant {
  variantId?: number;
  weight: number;
  unit: 'g' | 'kg' | 'l' | 'ml';
  price: number;
  stock: number;
}

interface Category {
  categorieId: number;
  name: string;
}

interface Product {
  productId: number;
  productName: string;
  productDescription?: string;
  productImage?: File | string | null;
  unit?: 'g' | 'kg';
  variants?: Variant[];
  categorieId?: number;
  category?: Category;
}

const initialForm: Product = {
  productId: 0,
  productName: '',
  productDescription: '',
  productImage: null,
  unit: 'g',
  variants: [],
  categorieId: undefined,
};

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<Product>(initialForm);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);

  // fetch categories
  const fetchCategories = async () => {
    try {
      const res = await apiClient.get('/Categories/GetCategories');
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  // fetch products
  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/AddProduct/GetAllProducts');
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      const err = error as AxiosError;
      if (err.response && err.response.status === 404) {
        setProducts([]);
      } else {
        console.error('Failed to fetch products:', error);
        alert('Failed to fetch products from the server.');
      }
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  // handle input change
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'file') {
      const fileInput = e.target as HTMLInputElement;
      const file = fileInput.files ? fileInput.files[0] : null;
      setForm((prev) => ({ ...prev, productImage: file }));
    } else {
      const val = type === 'number' ? parseInt(value) : value;
      setForm((prev) => ({ ...prev, [name]: val }));
    }
  };

  // handle variants
  const handleVariantChange = (index: number, field: keyof Variant, value: string | number) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const handleAddVariant = () => {
    setVariants([...variants, { weight: 0, unit: 'g', price: 0, stock: 0 }]);
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  // submit product
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.categorieId) {
      alert('Please select a category!');
      return;
    }

    const formData = new FormData();
    formData.append('ProductId', form.productId.toString());
    formData.append('ProductName', form.productName.trim());
    formData.append('ProductDescription', form.productDescription || '');
    formData.append('CategorieId', form.categorieId.toString());

    if (form.productImage instanceof File) {
      formData.append('imageFile', form.productImage);
    }

    const variantsJson = JSON.stringify(
      variants.map((v) => ({
        VariantId: v.variantId ?? 0,
        Weight: v.weight, // 🚀 direct weight save pannum
        Unit: v.unit,     // 🚀 unit g/kg ah store pannum
        Price: v.price,
        Stock: v.stock,
      }))
    );
    formData.append('VariantsJson', variantsJson);
    try {
      await apiClient.post(
        '/AddProduct/AddOrUpdateProductWithVariants',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      alert('Product saved successfully!');
      fetchProducts();
      handleReset();
    } catch (error) {
      console.error('Failed to save product:', error);
      alert('Something went wrong while saving the product.');
    }
  };

  // edit product
  const handleEdit = (idx: number) => {
    const prod = products[idx];

    setForm({
      productId: prod.productId,
      productName: prod.productName,
      productDescription: prod.productDescription || '',
      productImage: prod.productImage || null,
      categorieId: prod.categorieId ?? prod.category?.categorieId,
      unit: 'g'
    });

    // 🔹 Populate variants table
    setVariants(
      (prod.variants || []).map(v => ({
        variantId: v.variantId,
        weight: v.weight,
        unit: v.unit,
        price: v.price,
        stock: v.stock
      }))
    );

    setEditIndex(idx);

    // 🔹 Scroll to top UX)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // delete product
  const handleDelete = async (idx: number) => {
    const productToDelete = products[idx];
    if (window.confirm(`Are you sure you want to delete "${productToDelete.productName}"?`)) {
      try {
        await apiClient.delete(
          `/AddProduct/DeleteProduct?id=${productToDelete.productId}`
        );
        setProducts(products.filter((_, i) => i !== idx));
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Failed to delete product:', error);
        alert('Something went wrong while deleting the product.');
      }
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setVariants([]);
    setEditIndex(null);
  };
  const getMinPrice = (variants?: Variant[]) => {
    if (!variants || variants.length === 0) return '-';
    return Math.min(...variants.map(v => v.price));
  };

  const getTotalStock = (variants?: Variant[]) => {
    if (!variants || variants.length === 0) return 0;
    return variants.reduce((sum, v) => sum + v.stock, 0);
  };

  return (
    <div>
      <div className="py-1 bg-primary">
        <div className="container">
          <div className="row no-gutters d-flex align-items-start align-items-center px-md-0">
            <div className="col-lg-12 d-block">
              <div className="row d-flex">
                <div className="col-md pr-4 d-flex topper align-items-center">
                  <div className="icon mr-2 d-flex justify-content-center align-items-center"><FiPhone size={12} color="white" /></div>
                  <span className="text">+91 9148761750</span>
                </div>
                <div className="col-md pr-4 d-flex topper align-items-center">
                  <div className="icon mr-2 d-flex justify-content-center align-items-center"><FiSend size={12} color="white" /></div>
                  <span className="text">care@fhome.shop</span>
                </div>
                <div className="col-md-5 pr-4 d-flex topper align-items-center text-lg-right">
                  <span className="text">3-5 Business days delivery &amp; Free Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <nav className="navbar navbar-expand-lg navbar-dark ftco_navbar bg-dark ftco-navbar-light" id="ftco-navbar">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img
              src="/images/fhome-logo.jpg"
              alt=""
              style={{ height: "55px", marginRight: "8px", borderRadius: "50%", width: "80px" }}
            />
            <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#28a745' }}></span>
          </a>

          <div className="collapse navbar-collapse" id="ftco-nav">
            <ul className="navbar-nav ml-auto">
              <li className="nav-item active"><a href="/admin/categories" className="nav-link">AdminCategories</a></li>
              <li className="nav-item active"><a href="/admin/deal-of-the-day" className="nav-link">DealOfTheDay</a></li>
              <li className="nav-item"><a href="/admin" className="nav-link">Admin Products</a></li>
              <li className="nav-item"><a href="/admin/orders" className="nav-link">Admin Orders</a></li>
            </ul>
          </div>
        </div>
      </nav>
      <div className="container">
        <h3 className="mb-4">{editIndex !== null ? 'Edit Product' : 'Add Product'}</h3>
        <form onSubmit={handleSubmit} onReset={handleReset}>
          {/* Name */}
          <div className="form-group">
            <label>Product Name</label>
            <input
              type="text"
              className="form-control"
              name="productName"
              value={form.productName}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Category */}
          <div className="form-group">
            <label>Category</label>
            <select
              name="categorieId"
              className="form-select"
              value={form.categorieId || ''}
              onChange={handleInputChange}
            >
              <option value="">-- Select Category --</option>
             {categories.map(cat => (
                <option key={cat.categorieId} value={cat.categorieId}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Variants */}
          <h5>Variants</h5>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Weight</th>
                <th>Unit</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={variant.weight}
                      onChange={(e) => handleVariantChange(idx, 'weight', parseInt(e.target.value))}
                    />
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={variant.unit}
                      onChange={(e) => handleVariantChange(idx, 'unit', e.target.value as 'g' | 'kg' | 'l' | 'ml')}
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="l">L</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={variant.price}
                      onChange={(e) => handleVariantChange(idx, 'price', parseInt(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={variant.stock}
                      onChange={(e) => handleVariantChange(idx, 'stock', parseInt(e.target.value))}
                    />
                  </td>
                  <td>
                    <button type="button" className="btn btn-danger" onClick={() => handleRemoveVariant(idx)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn btn-primary" onClick={handleAddVariant}>
            + Add Variant
          </button>

          {/* Description */}
          <div className="form-group mt-3">
            <label>Description</label>
            <textarea
              className="form-control"
              name="productDescription"
              value={form.productDescription}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Image */}
          <div className="form-group">
            <label>Product Image</label>
            <input
              type="file"
              className="form-control"
              name="productImage"
              accept="image/*"
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group mt-3">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="reset" className="btn btn-secondary ml-2">Reset</button>
          </div>
        </form>

        {/* Product Table */}
        <h3 className="mt-4">Product List</h3>
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Description</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, idx) => (
                <tr key={idx}>
                  <td>{product.productName}</td>

                  <td>{product.category?.name || '-'}</td>

                  {/* ✅ PRICE FROM VARIANTS */}
                  <td>₹ {getMinPrice(product.variants)}</td>

                  {/* ✅ STOCK FROM VARIANTS */}
                  <td>{getTotalStock(product.variants)}</td>

                  <td>{product.productDescription || '-'}</td>

                  <td>
                    {product.productImage ? (
                      <img
                        src={
                          typeof product.productImage === 'string'
                            ? product.productImage
                            : URL.createObjectURL(product.productImage)
                        }
                        alt={product.productName}
                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                      />
                    ) : 'No image'}
                  </td>

                  <td>
                    <button className="btn btn-sm btn-info" onClick={() => handleEdit(idx)}>
                      Edit
                    </button>{' '}
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(idx)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {products.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center">No products available.</td>
                </tr>
              )}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;

